"use client"

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Video, Monitor, Lock } from 'lucide-react'
import { useLanguage } from '@/lib/i18n/language-provider'

interface ExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onExport: (options: WebCodecsExportOptions) => void
  onDurationChange?: (duration: number) => void
  defaultFps?: number
  canvasWidth?: number
  canvasHeight?: number
  isWebCodecsSupported?: boolean
  isTauriApp?: boolean // NEW: Detect if running in Tauri
  isMultiGrid?: boolean // NEW: Show export mode option for MultiGrid
  gridCount?: number // NEW: Number of grids for MultiGrid
}

export type OutputFormat = 'MP4' | 'MOV' | 'ProRes' | 'WebM'

export type ExportMode = 'full-canvas' | 'individual-grids'

export interface WebCodecsExportOptions {
  fps: number
  duration: number
  outputFormat: OutputFormat
  loopDuration?: number // Loop duration in seconds (3, 5, or 10)
  fpsForFilename?: number // FPS value for filename display
  exportMode?: ExportMode // Pour MultiGrid : canvas complet ou grilles individuelles
}

// Keep old interface for backward compatibility
export interface ExportOptions {
  fps: number
  duration: number
  format: 'MP4' | 'MOV' | 'WebM' | 'ProRes'
  codec: 'H264' | 'H265' | 'VP9'
  qualityPreset: string
  crf: number
  encoder: 'NVENC' | 'QuickSync' | 'AMF' | 'VideoToolbox' | 'Software'
}

export function FfmpegExportDialog({
  open,
  onOpenChange,
  onExport,
  onDurationChange,
  defaultFps = 60,
  canvasWidth = 0,
  canvasHeight = 0,
  isWebCodecsSupported = true,
  isTauriApp = false,
  isMultiGrid = false,
  gridCount = 0
}: ExportDialogProps) {
  const { t, language } = useLanguage()

  // Determine default FPS based on language (50 for FR, 60 for EN)
  const getDefaultFps = () => language === 'fr' ? 50 : 60

  const defaultFpsValue = getDefaultFps()
  const [fps, setFps] = useState(defaultFpsValue)
  const [fpsInput, setFpsInput] = useState(defaultFpsValue.toString())
  const [duration, setDuration] = useState(5)
  const [outputFormat, setOutputFormat] = useState<OutputFormat>(isTauriApp ? 'MP4' : 'WebM')
  const [exportMode, setExportMode] = useState<ExportMode>('full-canvas')

  // Synchroniser avec defaultFps quand le dialogue s'ouvre ou que la langue change
  useEffect(() => {
    if (open) {
      setFps(defaultFpsValue)
      setFpsInput(defaultFpsValue.toString())
      // Reset format based on platform
      if (!isTauriApp && outputFormat !== 'WebM') {
        setOutputFormat('WebM')
      }
    }
  }, [open, defaultFpsValue, language, isTauriApp, outputFormat])

  const handleFpsChange = (value: number) => {
    // Round to nearest multiple of 5
    const roundedValue = Math.round(value / 5) * 5
    const clampedValue = Math.max(5, Math.min(120, roundedValue))
    setFps(clampedValue)
    setFpsInput(clampedValue.toString())
  }

  const handleInputChange = (value: string) => {
    setFpsInput(value)
    const numValue = parseInt(value)
    if (!isNaN(numValue)) {
      handleFpsChange(numValue)
    }
  }

  const handleInputBlur = () => {
    const numValue = parseInt(fpsInput)
    if (isNaN(numValue) || numValue < 5 || numValue > 120) {
      setFpsInput(fps.toString())
    } else {
      // Round to nearest multiple of 5 on blur
      handleFpsChange(numValue)
    }
  }

  const handleDurationChange = (newDuration: number) => {
    setDuration(newDuration)
    if (onDurationChange) {
      onDurationChange(newDuration)
    }
  }

  const handleFormatSelect = (format: OutputFormat) => {
    // On Web, only WebM is allowed
    if (!isTauriApp && format !== 'WebM') {
      return
    }
    setOutputFormat(format)
  }

  const handleExport = () => {
    onExport({
      fps,
      duration: duration * 1000, // Convert to ms
      outputFormat,
      loopDuration: duration, // Pass loop duration in seconds
      fpsForFilename: fps, // Pass fps for filename display
      exportMode: isMultiGrid ? exportMode : 'full-canvas', // Only send exportMode for MultiGrid
    })
    onOpenChange(false)
  }

  // Estimate export time (rough)
  const estimateExportTime = (): string => {
    const totalFrames = fps * duration
    // Rough estimate: ~30-60 fps encoding speed
    let encodingSpeed = 45
    let conversionTime = 0
    
    if (isTauriApp) {
      if (outputFormat === 'ProRes') {
        conversionTime = totalFrames / 30 // ProRes is slower
      } else if (outputFormat !== 'WebM') {
        conversionTime = totalFrames / 60
      }
    }
    
    const estimatedSeconds = (totalFrames / encodingSpeed) + conversionTime
    if (estimatedSeconds < 60) {
      return `~${Math.ceil(estimatedSeconds)}s`
    }
    return `~${Math.ceil(estimatedSeconds / 60)}min`
  }

  // Get format description
  const getFormatDescription = (format: OutputFormat): string => {
    switch (format) {
      case 'MP4':
        return t('export.dialog.format.h264mp4.desc')
      case 'MOV':
        return t('export.dialog.format.h264mov.desc')
      case 'ProRes':
        return t('export.dialog.format.proresmov.desc')
      case 'WebM':
        return t('export.dialog.format.webm.desc')
    }
  }

  // Get format display name
  const getFormatName = (format: OutputFormat): string => {
    switch (format) {
      case 'MP4':
        return t('export.dialog.format.h264mp4')
      case 'MOV':
        return t('export.dialog.format.h264mov')
      case 'ProRes':
        return t('export.dialog.format.proresmov')
      case 'WebM':
        return 'VP9 WebM'
    }
  }

  // Check if format is available
  const isFormatAvailable = (format: OutputFormat): boolean => {
    if (isTauriApp) return true
    return format === 'WebM'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            {t('export.dialog.title')}
          </DialogTitle>
          <DialogDescription>
            {t('export.dialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Output Format Selection */}
          <div className="space-y-3">
            <Label className="font-semibold">{t('export.dialog.codecOutput')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {/* WebM - Always available */}
              <button
                type="button"
                onClick={() => handleFormatSelect('WebM')}
                className={`p-3 border-2 rounded-lg text-center transition-all ${
                  outputFormat === 'WebM'
                    ? 'border-primary bg-primary/10'
                    : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <span className="font-semibold text-sm">VP9 WebM</span>
              </button>

              {/* H.264 MP4 - Tauri only */}
              <button
                type="button"
                onClick={() => handleFormatSelect('MP4')}
                disabled={!isTauriApp}
                className={`p-3 border-2 rounded-lg text-center transition-all relative ${
                  !isTauriApp 
                    ? 'border-gray-200 dark:border-gray-800 opacity-50 cursor-not-allowed bg-muted'
                    : outputFormat === 'MP4'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <span className="font-semibold text-sm">{getFormatName('MP4')}</span>
                {!isTauriApp && (
                  <Lock className="absolute top-1 right-1 h-3 w-3 text-muted-foreground" />
                )}
              </button>

              {/* H.264 MOV - Tauri only */}
              <button
                type="button"
                onClick={() => handleFormatSelect('MOV')}
                disabled={!isTauriApp}
                className={`p-3 border-2 rounded-lg text-center transition-all relative ${
                  !isTauriApp 
                    ? 'border-gray-200 dark:border-gray-800 opacity-50 cursor-not-allowed bg-muted'
                    : outputFormat === 'MOV'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <span className="font-semibold text-sm">{getFormatName('MOV')}</span>
                {!isTauriApp && (
                  <Lock className="absolute top-1 right-1 h-3 w-3 text-muted-foreground" />
                )}
              </button>

              {/* ProRes MOV - Tauri only */}
              <button
                type="button"
                onClick={() => handleFormatSelect('ProRes')}
                disabled={!isTauriApp}
                className={`p-3 border-2 rounded-lg text-center transition-all relative ${
                  !isTauriApp 
                    ? 'border-gray-200 dark:border-gray-800 opacity-50 cursor-not-allowed bg-muted'
                    : outputFormat === 'ProRes'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                }`}
              >
                <span className="font-semibold text-sm">{getFormatName('ProRes')}</span>
                {!isTauriApp && (
                  <Lock className="absolute top-1 right-1 h-3 w-3 text-muted-foreground" />
                )}
              </button>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {getFormatDescription(outputFormat)}
            </p>

            {/* Desktop-only message */}
            {!isTauriApp && (
              <div className="flex items-center gap-2 p-2 bg-muted rounded-lg text-xs text-muted-foreground">
                <Monitor className="h-4 w-4 flex-shrink-0" />
                <span>{t('export.dialog.desktopOnly')}</span>
              </div>
            )}
          </div>

          {/* Export Mode (MultiGrid only) */}
          {isMultiGrid && gridCount > 0 && (
            <div className="space-y-3">
              <Label className="font-semibold">{t('export.dialog.exportMode') || 'Mode d\'export'}</Label>
              <div className="grid grid-cols-1 gap-2">
                {/* Full Canvas */}
                <button
                  type="button"
                  onClick={() => setExportMode('full-canvas')}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    exportMode === 'full-canvas'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-semibold text-sm">{t('export.dialog.exportMode.fullCanvas') || 'Canvas complet'}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t('export.dialog.exportMode.fullCanvas.desc') || 'Exporter tout le canvas en une seule vidéo'}
                  </div>
                </button>

                {/* Individual Grids */}
                <button
                  type="button"
                  onClick={() => setExportMode('individual-grids')}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    exportMode === 'individual-grids'
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="font-semibold text-sm">
                    {t('export.dialog.exportMode.individualGrids') || 'Grilles individuelles'}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      ({gridCount} {gridCount > 1 ? (t('export.dialog.grids') || 'grilles') : (t('export.dialog.grid') || 'grille')})
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {t('export.dialog.exportMode.individualGrids.desc') || `Exporter chaque grille séparément (${gridCount} vidéos${!isTauriApp ? ' dans une archive ZIP' : ''})`}
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* FPS */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">{t('export.dialog.frameRate')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={fpsInput}
                  onChange={(e) => handleInputChange(e.target.value)}
                  onBlur={handleInputBlur}
                  min={5}
                  max={120}
                  className="w-20 h-9"
                />
                <span className="text-sm text-muted-foreground">{t('export.dialog.fps')}</span>
              </div>
            </div>
            <Slider
              value={[fps]}
              onValueChange={(v) => handleFpsChange(v[0])}
              min={5}
              max={120}
              step={5}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>5</span>
              <span>30</span>
              <span>60</span>
              <span>90</span>
              <span>120</span>
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-3">
            <Label className="font-semibold">{t('export.dialog.loopDuration')}</Label>
            <div className="flex gap-2">
              {[3, 5, 10].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => handleDurationChange(d)}
                  className={`flex-1 p-2 rounded-lg border-2 transition-all ${
                    duration === d
                      ? 'border-primary bg-primary/10'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600'
                  }`}
                >
                  <span className="text-sm font-medium">{d}s</span>
                </button>
              ))}
            </div>
          </div>

          {/* Info Summary */}
          <div className="bg-muted p-4 rounded-lg text-sm border">
            <div className="grid grid-cols-2 gap-y-2">
              <div className="text-muted-foreground">{t('export.dialog.resolution')}</div>
              <div className="font-medium text-right">{canvasWidth} × {canvasHeight}</div>

              <div className="text-muted-foreground">{t('export.dialog.totalFrames')}</div>
              <div className="font-medium text-right">{fps * duration}</div>

              <div className="text-muted-foreground">{t('export.dialog.estimatedTime')}</div>
              <div className="font-medium text-right">{estimateExportTime()}</div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('export.dialog.cancel')}
          </Button>
          <Button 
            onClick={handleExport} 
            variant="outline"
            className="bg-white text-black hover:bg-gray-100 dark:bg-white dark:text-black dark:hover:bg-gray-200 border-gray-300"
            disabled={!isWebCodecsSupported}
          >
            {t('export.dialog.export')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
